import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { ethers } from 'ethers';
import { useNetwork } from './NetworkContext';
import { NetworkConfig } from '../config/networks';

interface GovernanceContextType {
  contractAddress: string;
  setContractAddress: (address: string) => void;
  clearContractAddress: () => void;
  isValidAddress: (address: string) => boolean;
  isAddressSet: boolean;
}

const GovernanceContext = createContext<GovernanceContextType | undefined>(undefined);

export const useGovernance = () => {
  const context = useContext(GovernanceContext);
  if (context === undefined) {
    throw new Error('useGovernance must be used within a GovernanceProvider');
  }
  return context;
};

interface GovernanceProviderProps {
  children: ReactNode;
}

export const GovernanceProvider: React.FC<GovernanceProviderProps> = ({ children }) => {
  const [contractAddress, setContractAddressState] = useState<string>('');
  const { currentNetwork } = useNetwork();
  const previousNetworkRef = useRef<NetworkConfig | null>(null);

  // Load contract address from localStorage on mount
  useEffect(() => {
    const savedAddress = localStorage.getItem('governanceContractAddress');
    if (savedAddress) {
      setContractAddressState(savedAddress);
    }
  }, []);

  // Clear contract address when network changes
  useEffect(() => {
    if (currentNetwork && previousNetworkRef.current && 
        currentNetwork.chainId !== previousNetworkRef.current.chainId && 
        contractAddress) {
      // Clear the contract address when network actually changes
      setContractAddressState('');
      localStorage.removeItem('governanceContractAddress');
    }
    // Update the previous network reference
    previousNetworkRef.current = currentNetwork;
  }, [currentNetwork, contractAddress]);

  const setContractAddress = useCallback((address: string) => {
    setContractAddressState(address);
    localStorage.setItem('governanceContractAddress', address);
  }, []);

  const clearContractAddress = useCallback(() => {
    setContractAddressState('');
    localStorage.removeItem('governanceContractAddress');
  }, []);

  const isValidAddress = useCallback((address: string) => {
    return ethers.isAddress(address);
  }, []);

  const isAddressSet = contractAddress !== '';

  const value: GovernanceContextType = {
    contractAddress,
    setContractAddress,
    clearContractAddress,
    isValidAddress,
    isAddressSet,
  };

  return (
    <GovernanceContext.Provider value={value}>
      {children}
    </GovernanceContext.Provider>
  );
};
